import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMessage } from "../contexts/MessageContext";
import InventoryProductPhotosSection from "./InventoryProductPhotosSection";
import {
  lineSeparatedToStringArray,
  catalogPhotosFromInventoryApi,
  buildInventoryImagesSubmitBody,
  defaultProductPhotoAlt,
  normalizeCatalogImageUrlFrontend,
  specRowsToPayload,
} from "../utils/inventoryForm";

const defaultSpecRow = { label: "", value: "" };

const AddInventory = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [purchasePrice, setPurchaseprice] = useState("");
  const [rating, setRating] = useState("0");
  const [reviewCount, setReviewCount] = useState("0");
  const [isActive, setIsActive] = useState(true);
  const [tagsText, setTagsText] = useState("");
  const [highlightsText, setHighlightsText] = useState("");
  const [catalogPhotos, setCatalogPhotos] = useState([]);
  const [pendingRemovedS3Keys, setPendingRemovedS3Keys] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [specsRows, setSpecsRows] = useState([{ ...defaultSpecRow }]);

  const [categories, setCategories] = useState([]);
  const [lastCreatedProduct, setLastCreatedProduct] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showSuccess, showError } = useMessage();

  const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      logout();
      navigate("/login");
    }
    const fetchCategories = async () => {
      try {
        const t = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/category`, {
          headers: { "x-auth-token": t },
        });
        const data = await response.json();
        if (response.ok) {
          setCategories(data);
        } else {
          console.error("Failed to load categories:", data.message);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [apiUrl, logout, navigate]);

  const setSpec = (i, key, val) => {
    setSpecsRows((rows) => {
      const next = rows.slice();
      next[i] = { ...next[i], [key]: val };
      return next;
    });
  };

  const addSpecRow = () => setSpecsRows((r) => [...r, { ...defaultSpecRow }]);
  const removeSpecRow = (i) => {
    setSpecsRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, j) => j !== i)));
  };

  const removeCatalogPhoto = (clientId) => {
    const row = catalogPhotos.find((p) => p.clientId === clientId);
    if (!row) return;
    if (row.s3Key) setPendingRemovedS3Keys((prev) => [...prev, row.s3Key]);
    setCatalogPhotos((rows) => rows.filter((r) => r.clientId !== clientId));
  };

  const updateCatalogPhotoAlt = (clientId, alt) => {
    setCatalogPhotos((rows) =>
      rows.map((r) => (r.clientId === clientId ? { ...r, alt: String(alt) } : r))
    );
  };

  const uploadProductImages = async (files) => {
    if (!files?.length) return;
    const token = localStorage.getItem("token");
    if (!token) {
      showError("Please sign in again before uploading images.");
      return;
    }
    setUploading(true);
    try {
      const uploadedRows = [];
      for (const file of files) {
        const form = new FormData();
        form.append("file", file, file.name);
        form.append("productName", name.trim());
        const res = await fetch(`${apiUrl}/inventory/product-upload`, {
          method: "POST",
          headers: { "x-auth-token": token },
          body: form,
        });
        const rawText = await res.text();
        let body = {};
        try {
          body = rawText ? JSON.parse(rawText) : {};
        } catch {
          throw new Error(
            `Upload failed (HTTP ${res.status}). Could not parse API response — is REACT_APP_API_URL correct?`
          );
        }
        if (!res.ok || !body?.success || !body?.data?.imageUrl) {
          throw new Error(body?.message || `Failed to upload ${file.name}`);
        }
        const imageUrl = normalizeCatalogImageUrlFrontend(body.data.imageUrl);
        if (!imageUrl) {
          throw new Error(
            body?.message ||
              `Upload returned an unusable image URL (check PRODUCTS_ASSET_BASE_URL includes https://)—${file.name}`
          );
        }
        const apiAlt = typeof body.data.alt === "string" ? body.data.alt.trim() : "";
        uploadedRows.push({
          clientId: typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${imageUrl}-${Date.now()}-${Math.random()}`,
          url: imageUrl,
          alt: apiAlt || defaultProductPhotoAlt(name),
          s3Key: typeof body.data.key === "string" ? body.data.key : null,
        });
      }
      if (uploadedRows.length > 0) {
        setCatalogPhotos((prev) => {
          const next = [...prev];
          for (const r of uploadedRows) {
            if (!next.some((x) => x.url === r.url)) next.push(r);
          }
          return next;
        });
      }
      showSuccess(
        `Uploaded ${uploadedRows.length} image${uploadedRows.length === 1 ? "" : "s"} to S3 — shown below. Save the item to persist them and prune any removals from S3.`
      );
    } catch (error) {
      console.error(error);
      showError(error.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    const { images, removedProductS3Keys } = buildInventoryImagesSubmitBody(
      catalogPhotos,
      pendingRemovedS3Keys,
      name
    );
    const body = {
      name,
      description: description || null,
      shortDescription: shortDescription || null,
      longDescription: longDescription || null,
      slug: slug || null,
      categoryLabel: categoryLabel || null,
      quantity: Number(quantity) || 0,
      price: Number(price) || 0,
      purchasePrice: Number(purchasePrice) || 0,
      categoryId: categoryId || null,
      rating: Math.min(5, Math.max(0, Number(rating) || 0)),
      reviewCount: Math.max(0, Math.floor(Number(reviewCount) || 0)),
      isActive,
      tags: lineSeparatedToStringArray(tagsText),
      highlights: lineSeparatedToStringArray(highlightsText),
      images,
      removedProductS3Keys,
      specs: specRowsToPayload(specsRows),
    };
    try {
      const response = await fetch(`${apiUrl}/inventory/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        const imgCount = Array.isArray(data.images) ? data.images.length : 0;
        showSuccess(
          imgCount
            ? `Item added with ${imgCount} image URL${imgCount === 1 ? "" : "s"} on the record.`
            : "Inventory item added successfully."
        );
        if (data.uuid) {
          const fromDb = data.slug != null ? String(data.slug).trim() : "";
          const fromName =
            fromDb ||
            String(data.name || "")
              .toLowerCase()
              .trim()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "");
          setLastCreatedProduct({
            uuid: data.uuid,
            name: data.name || "New item",
            slug: fromName || "",
          });
        }
        setCatalogPhotos(catalogPhotosFromInventoryApi(data.images));
        setPendingRemovedS3Keys([]);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        showError(data.message || "Failed to add inventory item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      showError("Error adding item");
    }
  };

  const inputCls =
    "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";
  const sectionTitle = "text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4";

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-4xl mx-auto bg-white m-5 p-6 rounded shadow-md"
    >
      <h2 className="text-xl font-bold text-gray-800 mb-1">Add New Inventory Item</h2>
      <p className="text-sm text-gray-500 mb-6">
        Core rental fields, storefront text, and optional images/specs for the public catalog.
      </p>

      {lastCreatedProduct ? (
        <div className="mb-8 rounded-xl border border-blue-200 bg-blue-50/90 p-4 shadow-sm space-y-3">
          <p className="text-sm font-medium text-blue-950">
            Saved <span className="font-semibold">&quot;{lastCreatedProduct.name}&quot;</span> to inventory. The form
            below is unchanged — continue editing, add more items, or go to the list when you are ready.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Link
              to={`/inventory/${lastCreatedProduct.uuid}/edit`}
              className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
            >
              Open this product (staff edit)
            </Link>
            <span className="text-blue-950/85">
              ID <code className="rounded bg-white/80 px-1.5 py-0.5 text-xs">{lastCreatedProduct.uuid}</code>
              {lastCreatedProduct.slug ? (
                <>
                  {' '}
                  · Catalog slug{' '}
                  <code className="rounded bg-white/80 px-1.5 py-0.5 text-xs">{lastCreatedProduct.slug}</code>
                </>
              ) : null}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setLastCreatedProduct(null)}
            className="text-sm text-blue-800 hover:text-blue-950 underline underline-offset-2"
          >
            Dismiss this notice
          </button>
        </div>
      ) : null}

        <div className="space-y-4">
          <h3 className={sectionTitle}>Core</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Slug (optional — auto from name if empty)</label>
              <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Category *</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls} required>
                <option value="">-- Select Category --</option>
                {categories.map((cat) => (
                  <option key={cat.uuid} value={cat.uuid}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Display category override (legacy, optional)</label>
              <input type="text" value={categoryLabel} onChange={(e) => setCategoryLabel(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Quantity *</label>
              <input
                type="number"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Rental price *</label>
              <input
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Purchase price</label>
              <input
                type="number"
                step="0.01"
                value={purchasePrice}
                onChange={(e) => setPurchaseprice(e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="flex flex-col justify-end">
              <label className={labelCls}>Active on public catalog</label>
              <div className="flex items-center gap-2 min-h-[42px]">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  id="isActive"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Item is visible in the catalog
                </label>
              </div>
            </div>
            <div>
              <label className={labelCls}>Description (short internal / legacy one-liner)</label>
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Storefront text</h3>
          <div className="space-y-4">
            <label className={labelCls}>Short description (subtitle)</label>
            <textarea
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              className={inputCls}
              rows={2}
            />
            <label className={labelCls}>Long description (about)</label>
            <textarea
              value={longDescription}
              onChange={(e) => setLongDescription(e.target.value)}
              className={inputCls}
              rows={5}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Rating (0–5)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={rating}
                  onChange={(e) => setRating(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Review count</label>
                <input
                  type="number"
                  min="0"
                  value={reviewCount}
                  onChange={(e) => setReviewCount(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Tags &amp; highlights</h3>
          <label className={labelCls}>Tags (comma or newline)</label>
          <textarea
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            className={inputCls}
            rows={2}
            placeholder="Wedding, Outdoor"
          />
          <label className={labelCls}>Highlights (one per line)</label>
          <textarea
            value={highlightsText}
            onChange={(e) => setHighlightsText(e.target.value)}
            className={inputCls}
            rows={3}
            placeholder="Seats 8"
          />
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Product photos (catalog)</h3>
          <InventoryProductPhotosSection
            photos={catalogPhotos}
            onRemove={removeCatalogPhoto}
            onChangePhotoAlt={updateCatalogPhotoAlt}
            uploading={uploading}
            onFileInputChange={async (e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = "";
              await uploadProductImages(files);
            }}
            helpText={
              <p>
                Upload-only: files go to your API, then to S3 under <code className="text-xs bg-gray-100 px-1 rounded">products/</code>{' '}
                (bucket <code className="text-xs bg-gray-100 px-1 rounded">mler-products</code> /{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">PRODUCTS_S3_BUCKET</code>). Folder is set in{' '}
                <strong>Settings → Applications → Catalog assets</strong>. Public read on the bucket is required for storefront
                thumbnails. Max 15 MB per file. Removing a row queues S3 deletion when you click{' '}
                <strong>Add item</strong>.
              </p>
            }
          />
        </div>

        <div className="space-y-4">
          <h3 className={sectionTitle}>Specs</h3>
          {specsRows.map((row, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-end">
              <input
                type="text"
                placeholder="Label"
                value={row.label}
                onChange={(e) => setSpec(i, "label", e.target.value)}
                className={`${inputCls} flex-1 min-w-[120px]`}
              />
              <input
                type="text"
                placeholder="Value"
                value={row.value}
                onChange={(e) => setSpec(i, "value", e.target.value)}
                className={`${inputCls} flex-1 min-w-[120px]`}
              />
              <button
                type="button"
                onClick={() => removeSpecRow(i)}
                className="px-3 py-1 text-xs bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addSpecRow}
            className="text-sm text-blue-600 hover:underline"
          >
            + Add spec row
          </button>
        </div>

        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Add item
          </button>
          <button
            type="button"
            onClick={() => navigate("/inventory")}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Go to inventory list
          </button>
        </div>
    </form>
  );
};

export default AddInventory;
